import { api } from '../config/api';

import { Tag } from './tags.service';
import { Property } from './properties.service';

export interface Document {
  id: string;
  property_id: number;
  filename: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  document_type: string;
  description?: string;
  upload_date: string;
  extracted_data?: any;
  created_at: string;
  updated_at: string;
  document_date?: string;
  subscription_id?: number;
  // Email fields
  from_email?: string;
  email_subject?: string;
  email_received_at?: string;
  processing_status?: string;
  ocr_text?: string;
  suggested_copy_destination_name?: string;
  tags?: Tag[];
  property?: Property;
}

export interface DocumentUpload {
  file: File;
  property_id: number;
  document_type: string;
  description?: string;
}

export interface ShareDocumentsRequest {
  documentIds: number[];
  recipientEmail: string;
  subject?: string;
  message?: string;
}

export interface ShareDocumentsResponse {
  success: boolean;
  shareUrl: string;
  pinCode: string;
  expiresAt: string;
  documentCount: number;
  message?: string;
}

class DocumentsService {
  async uploadDocument(data: DocumentUpload): Promise<Document> {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.description) {
      formData.append('description', data.description);
    }

    // Build query parameters
    const params = new URLSearchParams();
    if (data.property_id && data.property_id !== 0) {
      params.append('property_id', data.property_id.toString());
    }
    params.append('document_type', data.document_type);

    const response = await api.post<Document>(`/documents/upload?${params.toString()}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getDocuments(options?: {
    property_id?: number;
    include_all?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<Document[]> {
    const params = options || {};
    const response = await api.get<Document[]>('/documents/', { params });
    return response.data;
  }

  async getDocument(id: string): Promise<Document> {
    const response = await api.get<Document>(`/documents/${id}`);
    return response.data;
  }

  async deleteDocument(id: string): Promise<void> {
    await api.delete(`/documents/${id}`);
  }

  async processDocument(id: string): Promise<Document> {
    const response = await api.post<Document>(`/documents/${id}/process`);
    return response.data;
  }

  async updateDocument(id: string, data: Partial<Document>): Promise<Document> {
    const response = await api.patch<Document>(`/documents/${id}`, data);
    return response.data;
  }

  getFileUrl(filename: string): string {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    return `${baseURL}/documents/files/${filename}`;
  }

  async downloadDocument(id: string): Promise<Blob> {
    const response = await api.get(`/documents/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async searchDocuments(query: string, limit: number = 50): Promise<{documents: Document[], count: number}> {
    const response = await api.post('/documents/search/ai', { query, limit });
    return response.data;
  }

  async shareDocuments(request: ShareDocumentsRequest): Promise<ShareDocumentsResponse> {
    const response = await api.post('/document-share/share', {
      document_ids: request.documentIds,
      recipient_email: request.recipientEmail,
      subject: request.subject || 'Documents shared with you',
      message: request.message || ''
    });

    return {
      success: response.data.success,
      shareUrl: response.data.share_url,
      pinCode: response.data.pin_code,
      expiresAt: response.data.expires_at,
      documentCount: response.data.document_count
    };
  }
}

export const documentsService = new DocumentsService();
